/**
 * Wiki space view — Confluence-style sidebar with a hierarchical page tree.
 *
 * Layout:
 *   ┌──────────────┬─────────────────────────────────────┐
 *   │ Spaces       │ Active space header                 │
 *   │ + New space  ├─────────────────────────────────────┤
 *   │  ─ Eng       │ Page tree                           │
 *   │  ─ Product   │   ▸ Onboarding                      │
 *   │              │     ▸ Day 1 checklist               │
 *   │              │   ▸ Runbooks                        │
 *   └──────────────┴─────────────────────────────────────┘
 *
 * Each page row has a hover "+ Add sub-page" affordance.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";

import { CreatePageModal } from "@/components/CreatePageModal";
import { CreateSpaceModal } from "@/components/CreateSpaceModal";
import { api } from "@/lib/api";
import type { PageSummary, Space } from "@/lib/types";

type PageNode = PageSummary & { children: PageNode[]; depth: number };

function buildTree(pages: PageSummary[]): PageNode[] {
  const byId = new Map<string, PageNode>();
  for (const p of pages) {
    byId.set(p.id, { ...p, children: [], depth: 0 });
  }
  const roots: PageNode[] = [];
  for (const p of pages) {
    const node = byId.get(p.id)!;
    if (p.parent_id && byId.has(p.parent_id)) {
      const parent = byId.get(p.parent_id)!;
      node.depth = parent.depth + 1;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  // Stable order: position, then title.
  const sortNodes = (arr: PageNode[]) => {
    arr.sort((a, b) => a.position - b.position || a.title.localeCompare(b.title));
    arr.forEach((n) => sortNodes(n.children));
  };
  sortNodes(roots);
  return roots;
}

export function WikiListPage() {
  const { spaceSlug } = useParams<{ spaceSlug?: string }>();
  const [spaceModalOpen, setSpaceModalOpen] = useState(false);
  const [pageModalOpen, setPageModalOpen] = useState(false);
  const [pageParent, setPageParent] = useState<{ id: string; title: string } | null>(null);

  const { data: spaces = [], isLoading: spacesLoading } = useQuery({
    queryKey: ["spaces"],
    queryFn: async () => (await api.get<Space[]>("/spaces")).data,
  });

  const activeSpace = spaces.find((s) => s.slug === spaceSlug) ?? spaces[0] ?? null;

  const { data: pages = [], isLoading: pagesLoading } = useQuery({
    queryKey: ["pages", activeSpace?.slug],
    queryFn: async () =>
      (await api.get<PageSummary[]>(`/spaces/${activeSpace!.slug}/pages`)).data,
    enabled: !!activeSpace,
  });

  const tree = useMemo(() => buildTree(pages), [pages]);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 24 }}>
      {/* Spaces sidebar */}
      <aside style={sidebar}>
        <div style={sectionLabel}>
          <span>Spaces</span>
          <button
            type="button"
            onClick={() => setSpaceModalOpen(true)}
            title="New space"
            style={iconBtn}
          >
            +
          </button>
        </div>
        {spacesLoading && <p style={{ color: "var(--mute)", fontSize: 12 }}>Loading…</p>}
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {spaces.map((sp) => {
            const isActive = sp.slug === activeSpace?.slug;
            return (
              <li key={sp.slug} style={{ marginBottom: 2 }}>
                <Link
                  to={`/wiki/spaces/${sp.slug}`}
                  style={{
                    display: "block",
                    padding: "6px 10px",
                    borderRadius: 4,
                    fontSize: 13,
                    color: isActive ? "var(--ink)" : "var(--body-mid)",
                    background: isActive ? "var(--surface-2)" : "transparent",
                    fontWeight: isActive ? 550 : 400,
                    textDecoration: "none",
                  }}
                >
                  {sp.name}
                </Link>
              </li>
            );
          })}
          {spaces.length === 0 && !spacesLoading && (
            <li style={{ color: "var(--mute)", fontSize: 12, padding: "6px 10px" }}>
              No spaces yet.
            </li>
          )}
        </ul>
      </aside>

      {/* Active space + tree */}
      <main>
        {!activeSpace ? (
          <div
            style={{
              border: "1px dashed var(--hairline)",
              borderRadius: 8,
              padding: 32,
              textAlign: "center",
              color: "var(--mute)",
            }}
          >
            <p style={{ margin: "0 0 12px", fontSize: 14 }}>
              No wiki spaces yet. Spaces group pages by team or topic — like Engineering, Product, or
              Design.
            </p>
            <button type="button" onClick={() => setSpaceModalOpen(true)} style={primaryBtn}>
              + Create your first space
            </button>
          </div>
        ) : (
          <>
            <header
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 16,
                marginBottom: 4,
              }}
            >
              <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
                {activeSpace.name}
              </h1>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  color: "var(--mute)",
                }}
              >
                {activeSpace.slug}
              </span>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                onClick={() => {
                  setPageParent(null);
                  setPageModalOpen(true);
                }}
                style={primaryBtn}
              >
                + New page
              </button>
            </header>
            {activeSpace.description && (
              <p style={{ color: "var(--mute)", fontSize: 13, margin: "0 0 20px" }}>
                {activeSpace.description}
              </p>
            )}

            <div
              style={{
                border: "1px solid var(--hairline)",
                borderRadius: 6,
                background: "var(--canvas)",
              }}
            >
              {pagesLoading ? (
                <p style={{ padding: 16, color: "var(--mute)", fontSize: 13 }}>Loading…</p>
              ) : tree.length === 0 ? (
                <div
                  style={{
                    padding: 24,
                    textAlign: "center",
                    color: "var(--mute)",
                    fontSize: 13,
                  }}
                >
                  No pages in this space yet.{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setPageParent(null);
                      setPageModalOpen(true);
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "var(--ink)",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontSize: 13,
                      padding: 0,
                    }}
                  >
                    Create the first one
                  </button>
                  .
                </div>
              ) : (
                <ul style={{ listStyle: "none", padding: 8, margin: 0 }}>
                  {tree.map((node) => (
                    <TreeRow
                      key={node.id}
                      node={node}
                      onAddSub={(parent) => {
                        setPageParent(parent);
                        setPageModalOpen(true);
                      }}
                    />
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </main>

      <CreateSpaceModal open={spaceModalOpen} onClose={() => setSpaceModalOpen(false)} />
      <CreatePageModal
        open={pageModalOpen}
        onClose={() => setPageModalOpen(false)}
        spaceId={activeSpace?.id ?? null}
        spaceSlug={activeSpace?.slug ?? null}
        parentId={pageParent?.id ?? null}
        parentTitle={pageParent?.title ?? null}
      />
    </div>
  );
}

function TreeRow({
  node,
  onAddSub,
}: {
  node: PageNode;
  onAddSub: (parent: { id: string; title: string }) => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <li>
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "5px 8px",
          paddingLeft: 8 + node.depth * 18,
          borderRadius: 4,
          background: hover ? "var(--surface-1)" : "transparent",
        }}
      >
        <span
          aria-hidden
          style={{
            width: 12,
            height: 12,
            borderRadius: 2,
            background: "rgba(237,82,203,0.18)",
            flexShrink: 0,
          }}
        />
        <Link
          to={`/wiki/pages/${node.id}`}
          style={{
            flex: 1,
            fontSize: 13.5,
            color: "var(--ink)",
            textDecoration: "none",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {node.title}
        </Link>
        {hover && (
          <button
            type="button"
            onClick={() => onAddSub({ id: node.id, title: node.title })}
            title="Add sub-page"
            style={subPageBtn}
          >
            + Sub-page
          </button>
        )}
        <time
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--mute)",
            flexShrink: 0,
          }}
        >
          {new Date(node.updated_at).toLocaleDateString()}
        </time>
      </div>
      {node.children.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {node.children.map((child) => (
            <TreeRow key={child.id} node={child} onAddSub={onAddSub} />
          ))}
        </ul>
      )}
    </li>
  );
}

const sidebar: CSSProperties = {
  borderRight: "1px solid var(--hairline)",
  paddingRight: 16,
  minHeight: "calc(100vh - 200px)",
};

const sectionLabel: CSSProperties = {
  display: "flex",
  alignItems: "center",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 8,
  padding: "0 10px",
};

const iconBtn: CSSProperties = {
  marginLeft: "auto",
  width: 22,
  height: 22,
  display: "inline-grid",
  placeItems: "center",
  background: "transparent",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  fontSize: 14,
  color: "var(--body-mid)",
  cursor: "pointer",
  lineHeight: 1,
};

const primaryBtn: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "7px 14px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const subPageBtn: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "2px 8px",
  fontSize: 11.5,
  color: "var(--body-mid)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  flexShrink: 0,
};
